<template>
  <base-modal
    name="newGallery"
    :title="$t('gallery.new_gallery_modal.title')"
    :subtitle="$t('gallery.new_gallery_modal.subtitle')"
    :save-text="$t('gallery.new_gallery_modal.save')"
    :loading="loading"
    @submit="createGallery($event)"
  >
    <!-- Tabs -->
    <v-tabs
      v-model="activeTab"
      color="primary"
      class="mb-6 rounded-lg d-flex justify-start"
      height="48"
    >
      <v-tab class="text-none font-weight-bold">{{ $t('gallery.new_gallery_modal.tab_details') }}</v-tab>
      <v-tab class="text-none font-weight-bold">{{ $t('gallery.new_gallery_modal.tab_monetization') }}</v-tab>
      <v-tab class="text-none font-weight-bold">{{ $t('gallery.new_gallery_modal.tab_visibility') }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Tab 1: Details -->
      <v-tab-item>
        <v-card flat>
          <v-card-text class="px-0">
            <!-- Titel -->
            <div class="mb-0">
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.title_label') }}</div>
              <v-text-field
                v-model="title"
                name="title"
                :placeholder="$t('gallery.new_gallery_modal.title_placeholder')"
                :rules="galleryNameRules"
                outlined
                required
                dense
                counter="60"
                hide-details="auto"
              />
            </div>

            <!-- Beschreibung -->
            <div>
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.description_label') }}</div>
              <v-textarea
                v-model="description"
                name="description"
                :placeholder="$t('gallery.new_gallery_modal.description_placeholder')"
                outlined
                dense
                :rules="galleryDescriptionRules"
                counter="2000"
                hide-details="auto"
                rows="4"
              ></v-textarea>
            </div>

            <!-- Altersfreigabe -->
            <div class="mb-6">
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.age_rating_label') }}</div>
              <v-select
                name="fsk"
                v-model="fsk"
                :items="fskItems"
                item-text="fsk"
                item-value="id"
                :placeholder="$t('gallery.new_gallery_modal.age_rating_placeholder')"
                outlined
                dense
                hide-details="auto"
              ></v-select>
            </div>

            <v-divider class="my-6"></v-divider>

            <!-- Cover -->
            <div class="mb-2">
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.cover_label') }}</div>

              <v-row class="ma-0">
                <!-- Preview -->
                <v-col
                  cols="12"
                  class="pa-0 pr-md-2"
                >
                  <v-card
                    dark
                    class="thumbnail-preview"
                    elevation="0"
                  >
                    <div class="thumbnail-preview-empty">
                      <v-icon
                        color="#404040"
                        size="48"
                        class="mb-2"
                      >
                        mdi-upload-off-outline
                      </v-icon>
                      <div class="text-caption grey--text">
                        {{ $t('gallery.new_gallery_modal.cover_after_creation') }}
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
            <v-divider class="my-6"></v-divider>
            <!-- Community Section -->
            <div class="mb-2">
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.community_label') }}</div>

              <v-card
                outlined
                class="pa-4"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-body-2 font-weight-bold mb-1">
                      {{ $t('gallery.new_gallery_modal.comments_toggle') }}
                      <v-icon
                        v-if="comment"
                        color="success"
                        small
                        class="ml-2"
                      >mdi-check-circle</v-icon>
                    </div>
                    <div class="text-caption grey--text">{{ $t('gallery.new_gallery_modal.comments_hint') }}</div>
                  </div>
                  <v-switch
                    v-model="comment"
                    name="comment"
                    color="primary"
                    hide-details
                    class="mt-0"
                  ></v-switch>
                </div>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: Monetarisierung -->
      <v-tab-item>
        <v-card flat>
          <v-card-text class="px-0">
            <!-- Zugangsmodell -->
            <div class="mb-6">
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.access_model') }}</div>

              <v-card
                outlined
                class="pa-4 mb-4"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-body-2 font-weight-bold mb-1">{{ $t('gallery.new_gallery_modal.in_subscription') }}</div>
                    <div class="text-caption grey--text">{{ $t('gallery.new_gallery_modal.in_subscription_hint') }}</div>
                  </div>
                  <v-switch
                    v-model="gallerySubscription"
                    name="subscription"
                    color="primary"
                    hide-details
                    class="mt-0"
                  ></v-switch>
                </div>
              </v-card>

              <!-- Abo-Pläne (wenn Abo aktiv) -->
              <div v-if="gallerySubscription">
                <div class="mb-3">
                  <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.available_plans') }}</div>
                  <v-select
                    multiple
                    v-model="selectedPlans"
                    :items="get('plans')"
                    item-text="name"
                    item-value="id"
                    :placeholder="$t('gallery.new_gallery_modal.plans_placeholder')"
                    :rules="[(v) => (v && v.length > 0) || this.$t('gallery.new_gallery_modal.plans_required')]"
                    outlined
                    dense
                    chips
                    hide-details="auto"
                    name="plans"
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip
                        color="primary"
                        small
                        close
                        @click:close="deletePlanFromGallery(item)"
                      >
                        {{ item.name }}
                      </v-chip>
                    </template>
                  </v-select>
                  <div class="text-caption grey--text mt-1">{{ $t('gallery.new_gallery_modal.plans_hint') }}</div>
                </div>

                <v-alert
                  type="info"
                  text
                  dense
                  icon="mdi-information-outline"
                >
                  {{ $t('gallery.new_gallery_modal.plans_info') }}
                </v-alert>
              </div>

              <!-- Einzelpreis (wenn nicht im Abo) -->
              <div v-else>
                <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.single_price_label') }}</div>
                <v-text-field
                  v-model="galleryPrice"
                  type="text"
                  pattern="[0-9]*"
                  inputMode="numeric"
                  name="price"
                  :placeholder="$t('gallery.new_gallery_modal.price_placeholder')"
                  prefix="€"
                  :rules="galleryPriceRules"
                  outlined
                  dense
                  required
                  hide-details="auto"
                ></v-text-field>
                <div class="text-caption grey--text mt-1">{{ $t('gallery.new_gallery_modal.price_free_or_min') }}</div>
              </div>
            </div>

          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 3: Sichtbarkeit -->
      <v-tab-item>
        <v-card flat>
          <v-card-text class="px-0">
            <!-- Sichtbarkeit Section -->
            <div class="mb-2">
              <div class="text-body-2 font-weight-medium mb-2">{{ $t('gallery.new_gallery_modal.visibility_label') }}</div>

              <v-card
                outlined
                class="pa-4"
              >
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="text-body-2 font-weight-bold mb-1">
                      {{ $t('gallery.new_gallery_modal.publish') }}
                      <v-icon
                        v-if="published"
                        color="success"
                        small
                        class="ml-2"
                      >mdi-check-circle</v-icon>
                    </div>
                    <div class="text-caption grey--text">{{ $t('gallery.new_gallery_modal.publish_hint') }}</div>
                  </div>
                  <v-switch
                    v-model="published"
                    name="published"
                    color="primary"
                    hide-details
                    class="mt-0"
                  ></v-switch>
                </div>
              </v-card>

            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

  </base-modal>
</template>

<script>
import APIService from '@/services/api.service';
import { mapGetters } from 'vuex';

export default {
  name: 'newGallery',

  components: {
    BaseModal: () => import('@/components/modal/baseModal')
  },

  data() {
    return {
      activeTab: 0,
      title: '',
      description: '',
      fsk: 0,
      comment: false,
      valid: false,
      published: false,
      loading: false,
      galleryCoverImage: [],
      fileRecordsForUpload: [],
      isDraggingImage: false,
      selectedPlans: [],
      galleryPrice: 0,
      gallerySubscription: false,
      galleryNameRules: [
        v => !!v || this.$t('gallery.new_gallery_modal.validation.name_required'),
        v => (v && v.length <= 60) || this.$t('gallery.new_gallery_modal.validation.name_max_length'),
        v => (v && v.length >= 2) || this.$t('gallery.new_gallery_modal.validation.name_min_length')
      ],
      galleryDescriptionRules: [
        v => !v || v.length <= 2000 || this.$t('gallery.new_gallery_modal.validation.description_max_length'),
      ],
      galleryPriceRules: [
        v => v === '' || /^\d+([.,]\d{1,2})?$/.test(String(v)) || this.$t('gallery.new_gallery_modal.validation.price_invalid'),
        v => {
          const price = parseFloat(String(v).replace(",", ".")) || 0;
          return price === 0 || price >= 3 || this.$t('gallery.new_gallery_modal.validation.price_min');
        },
      ],
    }
  },

  computed: {
    ...mapGetters(["get"]),
    fskItems() {
      return [
        { id: 0, fsk: this.$t('gallery.new_gallery_modal.fsk_none') },
        { id: 16, fsk: this.$t('gallery.new_gallery_modal.fsk_16') },
        { id: 18, fsk: this.$t('gallery.new_gallery_modal.fsk_18') },
      ]
    },
  },

  methods: {
    deletePlanFromGallery(item) {
      const index = this.selectedPlans.indexOf(item.id);
      if (index > -1) {
        this.selectedPlans.splice(index, 1);
      }
    },

    currentUser() {
      return this.$store.state.auth.status.loggedIn;
    },

    createGallery(e) {
      this.loading = true;

      const subscription = this.gallerySubscription;
      const price = subscription ? 0 : (parseFloat(String(this.galleryPrice).replace(",", ".")) || 0);

      if (price > 0 && price < 3) {
        APIService.setResponse({
          message: this.$t('gallery.new_gallery_modal.validation.price_min'),
          color: "red",
        });
        this.loading = false;
        return;
      }

      const payload = {
        title: this.title,
        published: this.published,
        description: this.description,
        price: price,
        subscription: subscription,
        fsk: this.fsk,
        comment: this.comment
      }
      // The API rejects a subscription item without a plan (422).
      if (subscription) {
        payload.plans = this.selectedPlans;
      }
      this.$store.dispatch('post',
        {
          endpoint: '/api/v1/galleries',
          state: 'galleries',
          event: payload
        }
      )
        .then((response) => {
          if (response) {
            this.galleryCoverUpload(response.result.id)
          }
          this.loading = false
          this.$store.dispatch("modal/close", 'newGallery')
          // The list view reloads with its own sort and page size.
          this.$emit('saved')
          this.resetForm()
        }).catch((error) => {
          this.loading = false
        })
    },

    galleryCoverUpload(id) {
      if (this.galleryCoverImage.length === 0) return;

      setTimeout(() => {
        this.$store
          .dispatch("uploadFiles", {
            endpoint: `/api/v1/galleries/${id}/uploadcover`,
            files: { image: this.galleryCoverImage },
            metadata: { width: 250, height: 250 },
            message: false,
          })
          .then((response) => {
            APIService.setResponse({
              message: this.$t('gallery.new_gallery_modal.response.cover_uploaded'),
              color: "blue",
            });
          })
          .catch((error) => {
            console.log("Error auf editGallery beim cover upload: \n", error)
            APIService.setResponse({ message: this.$t('gallery.new_gallery_modal.response.upload_failed'), color: "red" });
          });
      }, 100);
    },

    triggerImageInput() {
      if (this.$refs.image && this.$refs.image.$el) {
        const input = this.$refs.image.$el.querySelector('input[type="file"]');
        if (input) input.click();
      }
    },

    onDropImage(e) {
      this.isDraggingImage = false;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        this.galleryCoverImage = files;
      }
    },

    onBeforeDeleteImage(fileRecord) {
      var i = this.fileRecordsForUpload.indexOf(fileRecord);
      if (i !== -1) {
        this.fileRecordsForUpload.splice(i, 1);
      } else {
        if (confirm(this.$t('gallery.new_gallery_modal.response.confirm_delete_cover'))) {
          this.$refs.image.deleteFileRecord(fileRecord);
        }
      }
    },

    imageDeleted(fileRecord) {
      var i = this.fileRecordsForUpload.indexOf(fileRecord);
      if (i !== -1) {
        this.fileRecordsForUpload.splice(i, 1);
      } else {
        this.$refs.image.deleteUpload(this.uploadUrl, this.uploadHeaders, fileRecord);
      }
    },

    resetForm() {
      this.galleryCoverImage = [];
      this.fileRecordsForUpload = [];
      this.title = '';
      this.description = '';
      this.fsk = 0;
      this.published = false;
      this.galleryPrice = 0;
      this.gallerySubscription = false;
      this.selectedPlans = [];
      this.activeTab = 0;
    },

    close(name) {
      this.$store.dispatch("modal/close", name)
    }
  },

  mounted() {
    this.$store.dispatch("fetch", {
      endpoint: "/api/v1/plans",
      state: "plans",
    });
  },
}
</script>

<style scoped>
.gradient-avatar {
  background: linear-gradient(135deg, #00C3FF 0%, #0099CC 100%) !important;
}

/* Tab styling */
::v-deep .v-tabs {
  border-radius: 8px !important;
}

::v-deep .v-tab {
  text-transform: none !important;
}

/* Drop Zone */
.drop-zone {
  border: 2px dashed #404040;
  border-radius: 8px;
  background: #0f0f0f;
  transition: all 0.3s ease;
  cursor: pointer;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone:hover,
.drop-zone--active {
  border-color: #4a9eff;
  background: #151515;
}

.drop-zone-content {
  text-align: center;
  pointer-events: none;
}

.drop-zone-text {
  font-size: 16px;
  color: #b0b0b0;
  font-weight: 500;
}

.drop-zone-formats {
  font-size: 14px;
  color: #707070;
}

/* Thumbnail Preview */
.thumbnail-preview {
  background: #0f0f0f;
  border: 2px solid #404040;
  border-radius: 8px;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.thumbnail-preview-wrapper {
  width: 100%;
  padding: 16px;
}

.thumbnail-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
</style>
